import React, { useState } from 'react';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CssBaseline,
  IconButton,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RedeemIcon from '@mui/icons-material/Redeem';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LogoutIcon from '@mui/icons-material/Logout';
import PaidIcon from '@mui/icons-material/Paid';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { useAuth } from '../context/AuthContext';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Logo1 from '../assets/Logo1.png';
import Logo2 from '../assets/Logo2.png';

const drawerWidth = 240;
const collapsedWidth = 72;

/* ================= MENU CONFIG BY ROLE ================= */
const MENU_BY_ROLE = {
  ADMIN: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'ผู้สูงอายุ', icon: <PeopleIcon />, path: '/patients' },
    { text: 'จัดการอาสาสมัครดูแล', icon: <HealthAndSafetyIcon />, path: '/patients/assign-volunteer' },
    { text: 'จัดการข้อมูล อสม.', icon: <ManageAccountsIcon />, path: '/volunteers' },
    { text: 'เศรษฐกิจชุมชน', icon: <PaidIcon />, path: '/economic/dashboard' },
    { text: 'รายชื่อผู้ได้รับสวัสดิการ', icon: <VolunteerActivismIcon />, path: '/economic/welfare-recipients' },
    { text: 'รายชื่อผู้เปราะบาง', icon: <PsychologyAltIcon />, path: '/economic/vulnerable' },
    { text: 'ระดับการช่วยเหลือ', icon: <PeopleIcon />, path: '/economic/assistance-priority' },
    { text: 'แจกของ / Events', icon: <RedeemIcon />, path: '/events' },
  ],

  OFFICER: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'ผู้สูงอายุ', icon: <PeopleIcon />, path: '/patients' },
    { text: 'จัดการอาสาสมัครดูแล', icon: <HealthAndSafetyIcon />, path: '/patients/assign-volunteer' },
    { text: 'จัดการข้อมูล อสม.', icon: <ManageAccountsIcon />, path: '/volunteers' },
    { text: 'เศรษฐกิจชุมชน', icon: <PaidIcon />, path: '/economic/dashboard' },
    { text: 'รายชื่อผู้ได้รับสวัสดิการ', icon: <VolunteerActivismIcon />, path: '/economic/welfare-recipients' },
    { text: 'รายชื่อผู้เปราะบาง', icon: <PsychologyAltIcon />, path: '/economic/vulnerable' },
    { text: 'ระดับการช่วยเหลือ', icon: <PeopleIcon />, path: '/economic/assistance-priority' },
    { text: 'แจกของ / Events', icon: <RedeemIcon />, path: '/events' },
  ],

  VOLUNTEER: [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'ผู้สูงอายุ', icon: <PeopleIcon />, path: '/patients' },
    { text: 'บันทึกสุขภาพ', icon: <NoteAddIcon />, path: '/health-records/new' },
    { text: 'ระดับการช่วยเหลือ', icon: <PeopleIcon />, path: '/economic/assistance-priority' },
    { text: 'แจกของ', icon: <RedeemIcon />, path: '/events' },
  ],
};

/* ======================================================= */

function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const menus = MENU_BY_ROLE[user?.role] || [];

  const handleDrawerToggle = () => setMobileOpen(p => !p);
  const handleCollapseToggle = () => setCollapsed(p => !p);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  /* ================= Drawer Content ================= */
  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ===== Top / Menu ===== */}
      <Box>
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
          }}
        >
          <IconButton
            onClick={handleCollapseToggle}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Toolbar>

        <List>
          {menus.map(menu => (
            <ListItemButton
              key={menu.text}
              component={RouterLink}
              to={menu.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                {menu.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={menu.text} />}
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* ===== Bottom / Logos ===== */}
      <Box
        sx={{
          mt: 'auto',
          p: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
        }}
      >

        <Box
          component="img"
          src={Logo1}
          alt="logo-1"
          sx={{
            height: collapsed ? 60 : 90,
            maxWidth: '100%',
            objectFit: 'contain',
            opacity: 0.7,
            transition: '0.2s',
          }}
        />
        <Box
          component="img"
          src={Logo2}
          alt="logo-2"
          sx={{
            height: collapsed ? 60 : 90,
            maxWidth: '100%',
            objectFit: 'contain',
            opacity: 0.7,
            transition: '0.2s',
          }}
        />
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* ================= AppBar ================= */}
      <AppBar
        position="fixed"
        sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          {/* Mobile hamburger */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            ระบบฐานข้อมูลผู้สูงอายุในชุมชน Silpa Care+ : {user?.organization}
          </Typography>

          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.name}
          </Typography>

          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ================= Mobile Drawer ================= */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* ================= Desktop Drawer ================= */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            overflowX: 'hidden',
            transition: 'width 0.25s',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* ================= Main Content ================= */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: { md: `${currentDrawerWidth}px` },
          transition: 'margin-left 0.25s',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
